import React from 'react';
import { motion } from 'framer-motion';
import { LockClosedIcon, UserGroupIcon, GlobeAltIcon, EyeIcon } from '@heroicons/react/24/outline';
import { Tooltip } from 'react-tooltip';

const VisibilityIndicator = ({ 
  visibility = 'private', 
  size = 'sm',
  showLabel = false,
  interactive = false,
  onVisibilityChange,
  allowedVisibilities = ['private', 'organization', 'public']
}) => {
  const visibilityConfig = {
    private: {
      icon: LockClosedIcon,
      label: 'Private',
      description: 'Only visible to you',
      color: 'text-gray-500 dark:text-[#666666]',
      bgColor: 'bg-gray-100 dark:bg-[#1A1A1A]',
      borderColor: 'border-gray-300 dark:border-[#2A2A2A]'
    },
    organization: {
      icon: UserGroupIcon,
      label: 'Team',
      description: 'Visible to your organization',
      color: 'text-brand',
      bgColor: 'bg-brand/10',
      borderColor: 'border-brand/30'
    },
    public: {
      icon: GlobeAltIcon,
      label: 'Public',
      description: 'Visible to everyone',
      color: 'text-green-600 dark:text-green-500',
      bgColor: 'bg-green-100 dark:bg-green-900/20',
      borderColor: 'border-green-300 dark:border-green-800'
    },
    shared: {
      icon: EyeIcon,
      label: 'Shared',
      description: 'Shared with specific people',
      color: 'text-blue-600 dark:text-blue-500',
      bgColor: 'bg-blue-100 dark:bg-blue-900/20',
      borderColor: 'border-blue-300 dark:border-blue-800'
    }
  };

  const config = visibilityConfig[visibility] || visibilityConfig.private;
  const Icon = config.icon;

  const sizeClasses = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  const paddingClasses = {
    xs: 'p-0.5',
    sm: 'p-1',
    md: 'p-1.5',
    lg: 'p-2'
  };

  if (!interactive) {
    return (
      <div className="inline-flex items-center gap-1.5">
        <div
          className={`inline-flex items-center justify-center rounded-lg ${config.bgColor} ${paddingClasses[size]}`}
          data-tooltip-id={`visibility-${visibility}`}
          data-tooltip-content={config.description}
        >
          <Icon className={`${sizeClasses[size]} ${config.color}`} />
        </div>
        {showLabel && (
          <span className={`text-xs font-medium ${config.color}`}>
            {config.label}
          </span>
        )}
        <Tooltip id={`visibility-${visibility}`} place="top" />
      </div>
    );
  }

  return (
    <div className="relative inline-block">
      <motion.button
        onClick={() => {
          const currentIndex = allowedVisibilities.indexOf(visibility);
          const nextIndex = (currentIndex + 1) % allowedVisibilities.length;
          onVisibilityChange(allowedVisibilities[nextIndex]);
        }}
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${config.borderColor} ${config.bgColor} hover:opacity-80 transition-all duration-200`}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <Icon className={`${sizeClasses[size]} ${config.color}`} />
        <span className={`text-xs font-medium ${config.color}`}>
          {config.label}
        </span>
      </motion.button>
    </div>
  );
};

export const VisibilityBadge = ({ visibility, size = 'xs' }) => {
  const badges = {
    private: (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-[#1A1A1A] text-gray-600 dark:text-[#666666]">
        <LockClosedIcon className="w-3 h-3" />
      </span>
    ),
    organization: (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-brand/10 text-brand">
        <UserGroupIcon className="w-3 h-3" />
      </span>
    ),
    public: (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-500">
        <GlobeAltIcon className="w-3 h-3" />
      </span>
    )
  };

  return badges[visibility] || badges.private;
};

export default VisibilityIndicator;